import React, { useContext } from 'react';
import { OpenAPIObject, OperationObject, SchemaObject } from 'openapi3-ts';

import Context from '../../../contexts/docs';

import { BaseProps, SchemaObjectEntry } from '.';
import {
  getFormattedExample,
  getRouteFromDefinition,
  getSchemaNamePrefix,
} from './schemaUtils';
import CodeBlockV2 from '../CodeBlockV2';
import SchemaLayout from './SchemaLayout/SchemaLayout';

import styles from './index.module.scss';
import { normalizeSchemaForRendering } from './SchemaLayout/schemaUtils';

interface ResponseSchemaProps extends BaseProps {
  definition: OpenAPIObject;
  route: string;
  method: string;
  simplifyForBill: boolean;
}

const getResponseExample = (
  fullRouteObject: OperationObject,
  exampleIndex: number,
) => {
  const maybeExamples: SchemaObject['examples'] =
    //       Accept examples object with nested example objects (oas v3.1.x)
    fullRouteObject.responses['200'].content['application/json'].examples || {
      //       Accept single example object  (oas v3.0.x: https://www.openapis.org/blog/2021/02/16/migrating-from-openapi-3-0-to-3-1-0)
      //       Keep the `value` wrapper: SchemaRow reads `examples[key].value`
      //       for the per-field JSON button, which silently dies without it.
      'example-1': {
        value:
          fullRouteObject.responses['200'].content['application/json'].example,
      },
    };

  const responseAsString =
    maybeExamples != null
      ? getFormattedExample(maybeExamples, exampleIndex)
      : '';

  return { responseAsString, responseExamples: maybeExamples };
};

const getRequiredResponseProperties = (props, responseSchema: any) => {
  let requiredResponseProperties: string[];

  // In Core Exchange, we have to tell financial institutions that some properties are required.
  if (props.exchangeSpec != null || props.coreExchangeSpec != null) {
    const requiredResponseArray: Array<string[]> = Object.entries(
      responseSchema.required || [],
    );

    requiredResponseProperties = requiredResponseArray.map((property) => {
      return property[1];
    });
  } else {
    return [];
  }
  return requiredResponseProperties;
};

// ResponseSchema is used to display the response fields for a given route
// We only show the 200 response. The other response is a generic error object.
// Used both in docs and in code exchange docs.

// There's also some logic here to handle highlighting code blocks when clicking
// on the schema rows, and vice versa.
const ResponseSchema: React.FC<ResponseSchemaProps> = (props) => {
  const { selectedCode, dispatch } = useContext(Context);

  const [validLineNumbers, setValidLineNumbers] = React.useState<Set<number>>(
    new Set(),
  );

  // Basically, we're having all the attributes in the SchemaRow component
  // report back to us that they could be responsible for highlighted code.
  const handleSchemaRowLineNumbers = React.useCallback(
    (lineStart: number | null) => {
      if (lineStart !== null) {
        setValidLineNumbers((prev) => new Set(prev).add(lineStart));
      }
      // console.log('validLineNumbers', validLineNumbers);
    },
    [],
  );

  const fullRouteObject = getRouteFromDefinition(
    props.definition,
    props.route,
    props.method,
  );
  if (fullRouteObject == null) {
    return null;
  }
  // Only get the 200 response. The only other response is a generic error object.
  // Endpoints that succeed with an empty body (for example a 204) have no 200
  // schema to render, so callers should describe the response in prose instead.
  if (
    fullRouteObject.responses?.['200']?.content?.['application/json'] == null
  ) {
    return null;
  }
  const responseSchema = normalizeSchemaForRendering(
    fullRouteObject.responses['200'].content['application/json'].schema,
  );
  const responseEntries: Array<SchemaObjectEntry> = Object.entries(
    responseSchema?.properties || {},
  );

  let requiredResponseProperties: string[] = getRequiredResponseProperties(
    props,
    responseSchema,
  );

  const routeKebabCase = getSchemaNamePrefix(props.route, props.method);

  const { responseAsString, responseExamples } = getResponseExample(
    fullRouteObject,
    props.exampleIndex,
  );

  // Extra checks to make sure the clicks aren't coming from the code block
  const showLineStart =
    selectedCode.route === props.route && selectedCode.item !== '__CODE_CLICK__'
      ? selectedCode.startLine
      : null;
  const showLineEnd =
    selectedCode.route === props.route && selectedCode.item !== '__CODE_CLICK__'
      ? selectedCode.endLine
      : null;

  // Handler for reverse highlighting (CodeBlock -> SchemaRow)
  const handleLineClick = (lineNumber: number) => {
    dispatch({
      type: 'SET_SELECTED_CODE',
      payload: {
        route: props.route,
        item: '__CODE_CLICK__', // Special identifier for code clicks
        startLine: lineNumber - 1,
        endLine: lineNumber - 1,
      },
    });
  };

  return (
    <SchemaLayout
      exchangeSpec={props.exchangeSpec}
      coreExchangeSpec={props.coreExchangeSpec}
      route={props.route}
      schemaName={`${routeKebabCase}-response`}
      tableOnly={props.tableOnly}
      expandAll={!props.collapse}
      expandText={props.expandText}
      hideText={props.hideText}
      unhideFlag={props.unhideFlag}
      simplify={props.simplifyForBill}
      entries={responseEntries}
      schema={responseSchema}
      responseExamples={responseExamples}
      stringCode={responseAsString}
      type='response'
      required={requiredResponseProperties}
      headerText='Response fields'
      onSchemaRowLineNumbers={handleSchemaRowLineNumbers}
    >
      {props.children != null ? (
        <div className={styles.codeBlock}>{props.children}</div>
      ) : (
        <CodeBlockV2
          className={styles.codeBlock}
          code={responseAsString}
          lang='json'
          title={
            props.exampleTitle != null ? props.exampleTitle : 'Response Object'
          }
          showLineStart={showLineStart}
          showLineEnd={showLineEnd}
          onLineClick={handleLineClick}
          validLineNumbers={validLineNumbers}
        />
      )}
    </SchemaLayout>
  );
};

export default ResponseSchema;
